import type { Dtos, UserId } from "@inrev/common";
import { useAdminFetchAdminUsers } from "@portal/api/admin-user/admin";
import { useAdminFetchAgencyPreviews } from "@portal/api/agency/admin";
import { FormItemLabel } from "@portal/components/form";
import { FormDropdownComponent } from "@portal/components/form/fieldLayouts";
import { FormSearchableInputWithDropdown } from "@portal/components/form/fields";
import { DropdownInput } from "@portal/components/form/inputs/DropdownInput";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Tooltip } from "react-tooltip";
import { QuickSubmitFormItemGroup } from "../../../../account/create/quick-submit/QuickSubmitFormItemGroup";
import { assigneeUserFromQuery } from "../../../../shared/AdminAssigneeControls";
import { ActionItemSelectAssignee } from "../../../../shared/tools/actionItems/components/ActionItemSelectAssignee";
import type { AdminBondRequestFormData } from "../../schema";

const EMPTY_OPTIONS: { label: string; value: string }[] = [];

export const AgencySection = ({
	agentPreviews,
}: {
	agentPreviews: Dtos.Agent.Preview.Get.Response[] | undefined;
}) => {
	const { agencyPreviews, agencyPreviewsLoading } = useAdminFetchAgencyPreviews();
	const formMethods = useFormContext<AdminBondRequestFormData>();
	const { control, watch, setValue } = formMethods;
	const { adminUsers = [] } = useAdminFetchAdminUsers();
	const selectedAgencyId = watch("agencyId");

	const defaultUnderwriterId = useMemo(() => {
		if (selectedAgencyId && agencyPreviews) {
			return agencyPreviews.find((agency) => agency.id === selectedAgencyId)?.underwriterUserId;
		}
	}, [selectedAgencyId, agencyPreviews?.length]);

	return (
		<>
			<QuickSubmitFormItemGroup className="flex-grow" data-testid="agency">
				<FormItemLabel name="agencyId">Agency</FormItemLabel>
				{agencyPreviewsLoading || !agencyPreviews ? (
					<CenteredSpinner />
				) : (
					<FormSearchableInputWithDropdown
						control={control}
						name="agencyId"
						placeholder="Select Agency"
						options={agencyPreviews.map((agency) => ({
							value: agency.id,
							label: agency.displayName,
						}))}
						anchor="bottom start"
						compact
					/>
				)}
			</QuickSubmitFormItemGroup>
			<QuickSubmitFormItemGroup className="flex-grow" data-testid="agent">
				{agentPreviews ? (
					<FormDropdownComponent
						formMethods={formMethods}
						name="agentId"
						label="Agent"
						placeholder="Select Agent"
						optionsClassName="max-h-[400px]"
						options={agentPreviews.map((agent) => ({
							value: agent.id,
							label: agent.displayName,
						}))}
						anchor="bottom start"
						compact
					/>
				) : (
					<>
						<FormItemLabel>Agent</FormItemLabel>
						<Tooltip
							offset={24}
							id="agent-tooltip"
							content="Select an agency before setting an agent"
						/>
						<DropdownInput
							data-tooltip-id="agent-tooltip"
							options={EMPTY_OPTIONS}
							placeholder={agencyPreviewsLoading ? "Loading..." : "Select an agency"}
							disabled
							compact
						/>
					</>
				)}
			</QuickSubmitFormItemGroup>
			<QuickSubmitFormItemGroup data-testid="underwriter">
				<FormItemLabel>Underwriter</FormItemLabel>
				<div className="flex items-center justify-center flex-grow">
					{defaultUnderwriterId && selectedAgencyId ? (
						<ActionItemSelectAssignee
							defaultUnderwriterId={defaultUnderwriterId}
							userList={adminUsers.map(assigneeUserFromQuery)}
							selectedUserId={watch("underwriterId")}
							setUserId={(userId: UserId) => setValue("underwriterId", userId)}
						/>
					) : (
						<div className="flex items-center justify-center w-[28px] h-[28px] text-[12px] font-medium text-secondary border border-bc-strong bg-surface-neutral hover:text-primary hover:border-bc-stronger rounded-full opacity-50">
							??
						</div>
					)}
				</div>
			</QuickSubmitFormItemGroup>
		</>
	);
};
